import PropTypes from 'prop-types';
import classNames from 'classnames';
import DashboardCard from './cards/DashboardCard';

/**
 * StatCard
 * 
 * A KPI statistics card for the dashboard.
 * 
 * Props:
 *  - title: Card title
 *  - value: Big KPI figure (>= 0)
 *  - changePct: Month-on-month change percentage (can be 0 / null)
 *  - subValue: Optional secondary line under the KPI (e.g. breakdown text)
 *  - iconClass: Bootstrap-icon class, e.g. "bi bi-ev-station"
 *  - classNameList: Bootstrap column classes, e.g. "col-md-3"
 *  - accent: Optional accent color for top bar (passed to DashboardCard)
 *
 * If changePct === null → no footer line.
 * Icon sits in a fixed column beside the KPI so left/right spacing matches.
 */
export default function StatCard({
  title,
  value,
  changePct,
  subValue,
  iconClass = 'bi bi-circle',
  classNameList = 'col-md-3',
  accent = null,
}) {
  const pos = changePct > 0;
  const neg = changePct < 0;

  const valueText = value?.toLocaleString?.() ?? '0';
  const pctText = `${Math.abs(changePct)}% from last month`;
  const pctClasses = classNames({
    'text-success': pos,
    'text-danger': neg,
    'text-muted': changePct === 0,
  });

  const arrowIcon = pos
    ? 'bi bi-arrow-up'
    : neg
      ? 'bi bi-arrow-down'
      : '';

  const hasData = value > 0 || typeof changePct === 'number';

  return (
    <DashboardCard className={classNameList} accent={accent}>
      <div className="d-flex align-items-start gap-3">
        <div className="flex-grow-1 min-w-0">
          <h6 className={classNames('fw-semibold', hasData ? 'text-body' : 'text-muted')}>
            {title}
          </h6>
          <div
            className={classNames('display-6 fw-bold', { 'text-muted': !hasData })}
            style={{ lineHeight: 1.1 }}
          >
            {valueText}
          </div>
          {subValue ? (
            <div className="text-muted small mt-1">{subValue}</div>
          ) : null}
          {typeof changePct === 'number' && (
            <div className={classNames('mt-1 fw-medium small', pctClasses)}>
              {arrowIcon && <i className={`${arrowIcon} me-1`} />}
              {pctText}
            </div>
          )}
        </div>
        <div className="flex-shrink-0 pt-1" aria-hidden="true">
          <i
            className={classNames(
              iconClass,
              'fs-2 lh-1 d-block',
              hasData ? 'text-primary' : 'text-muted opacity-25'
            )}
          />
        </div>
      </div>
    </DashboardCard>
  );
}

StatCard.propTypes = {
  title: PropTypes.string.isRequired,
  value: PropTypes.number,
  changePct: PropTypes.number,
  subValue: PropTypes.string,
  iconClass: PropTypes.string,
  classNameList: PropTypes.string,
  accent: PropTypes.string,
};
